<template>
    <div class="horse-preview-main-data flex justify-content-between align-items-center gap-4">
        <div class="horse-photo-container">
            <img :src="selectedHorse.photoUrl ? selectedHorse.photoUrl : getHorsePhoto()"
                 :alt="$t('message.horsePreview.horsePhoto')" class="horse-photo">
            <div v-if="selectedHorse.color" class="horse-color">{{ selectedHorse.color }}</div>
        </div>
        <div class="flex justify-content-between w-full p-2 gap-1">
            <div class="flex flex-column w-full align-items-center justify-content-between gap-2">
                <div class="flex flex-column align-items-center gap-1 pb-2">
                    <div class="horse-name text-center" v-html="formattedFullName"/>
                    <div class="arabian-name text-center">{{ selectedHorse.localName }}</div>
                </div>
                <div class="label-and-value">
                    <span class="label">{{ $t('message.horsePreview.ueln') }}</span>
                    <span class="value">{{ selectedHorse.ueln }}</span>
                </div>
                <div class="label-and-value">
                    <span class="label">{{ $t('message.horsePreview.microchip') }}</span>
                    <span class="value">{{ selectedHorse.transponderCode }}</span>
                </div>
                <div v-if="selectedHorse.secondTransponderCode" class="label-and-value second-transponder-code">
                    <span class="value text-right">{{ selectedHorse.secondTransponderCode }}</span>
                </div>
                <div v-if="selectedHorse.imported" class="label-and-value">
                    <span class="label">{{ $t('message.horsePreview.imported') }}</span>
                </div>
                <div class="flex align-items-center gap-3 h-full flex-wrap justify-content-center">
                    <CustomButton :label="$t('message.horsePreview.editHorse')" @click="goToEditHorse" secondary v-if="canEditHorse" />
                    <CustomButton :label="$t('message.horsePreview.delete')" @click="showDeleteDialog" v-if="canDeleteHorse"/>
                    <qrcode-vue class="qr-code-hidden" ref="qrLabHorseDetails" :value="qrLabHorseUrl" :size="qr_size" level="H" render-as="svg"/>
                    <CustomButton :label="$t('message.horsePreview.qrCodeDownloadButton')" @click="downloadSvg('qrLabHorseDetails', getLabSvgFileName)" v-if="selectedHorse.horseStatus !== 'EXTERNAL'"/>
                </div>
            </div>
            <img v-if="genderIconPath" :src="genderIconPath" :alt="$t('message.horsePreview.genderIcon')" class="gender-icon">
            <div v-else class="dummy" style="width: 100%;"></div>
        </div>
        <Dialog v-model:visible="deleteDialogStatus" header="Delete horse?" modal :draggable="false"
                :closable="true" style="max-width: 500px" >
            <span>
                Are you sure you want to remove
                <strong>{{ selectedHorse.name }}</strong>?
            </span>

            <template #footer>
                <CustomButton :label="$t('message.horsePreview.cancel')" secondary @click="hideDeleteDialog"/>
                <CustomButton :label="$t('message.horsePreview.delete')" @click="deleteHorse" autofocus/>
            </template>
        </Dialog>
    </div>
</template>

<script>
import QrcodeVue, { QrcodeCanvas, QrcodeSvg } from "qrcode.vue";
import Toast from "primevue/toast";
import Dialog from "primevue/dialog";
import { deleteHorse } from "@/swagger/vue-api-client";
import CustomButton from "@/components/form/CustomButton.vue";
import {HorseUtils} from "@/utils/HorseUtils";
import {SystemRole} from "@/utils/SystemRole";
import {HorseStatus} from "@/utils/HorseStatus";
import {FileUtils} from "@/utils/FileUtils";

export default {
    name: "HorsePreviewMainData",
    components: {
        Dialog,
        CustomButton,
        Toast,
        QrcodeVue,
    },

    props: {
        selectedHorse: {
            type: Object,
            required: true,
        },
    },

    emits: ["delete-element"],

    data() {
        return {
            deleteDialogStatus: false,
            qr_value: "https://example.com",
            qr_size: 200,
            qr_download_size: 512,
        };
    },

    methods: {
        getHorsePhoto() {
            if (document.body.classList.contains("arab-horses")) return require("../../../../assets/horse-graphic.png");
            return require("../../../../assets/Preview-Image-thoroughbred.jpg");
        },
        showDeleteDialog() {
            this.deleteDialogStatus = true;
        },
        hideDeleteDialog() {
            this.deleteDialogStatus = false;
        },
        notifyDelete() {
            this.$emit("delete-element");
            this.$toast.add({
                severity: "success",
                summary: this.$t("message.other.success"),
                detail: this.$t("message.other.elementDeleted"),
                life: 3000,
            });
        },
        notifyError(error) {
            const message = error.response?.data?.message || this.$t("unknownError");
            this.$toast.add({
                severity: "error",
                summary: this.$t("message.other.error"),
                detail: message,
                life: 5000,
            });
        },

        async deleteHorse() {
            this.hideDeleteDialog();
            try {
                await deleteHorse({ id: this.selectedHorse.id });
                this.notifyDelete();
            } catch (error) {
                this.notifyError(error);
            }
        },
        goToEditHorse() {
            if (this.selectedHorse.horseStatus === HorseStatus.EXTERNAL || this.selectedHorse.horseStatus === HorseStatus.NEW_IMPORTED) {
                this.$router.push({path: "/horse/import/" + this.selectedHorse.id});
            } else if (this.selectedHorse.horseStatus === HorseStatus.NEW_FOAL || this.selectedHorse.horseStatus === HorseStatus.WAITING_FOR_MARKING_DATA
                || this.selectedHorse.horseStatus === HorseStatus.LABORATORY_DATA_ENTERED) {
                this.$router.push({path: "/horse/foal/" + this.selectedHorse.id});
            } else {
                this.$router.push({ name: "horseEditMain", params: { id: this.selectedHorse.id } });
            }
        },
        downloadSvg(refName, fileName) {
            const qrComponent = this.$refs[refName];

            let svgElement = qrComponent.$el;
            if (svgElement.nodeName.toLowerCase() !== "svg") {
                svgElement = svgElement.querySelector("svg");
            } else {
                console.error("SVG element not found!");
            }

            const clonedSvg = svgElement.cloneNode(true);

            // Update the width and height attributes to the desired download size
            if (this.qr_download_size) {
                clonedSvg.setAttribute("width", this.qr_download_size);
                clonedSvg.setAttribute("height", this.qr_download_size);
            }

            const serializer = new XMLSerializer();
            const svgData = serializer.serializeToString(clonedSvg);

            const blob = new Blob([svgData], { type: "image/svg+xml;charset=utf-8" });
            const url = URL.createObjectURL(blob);

            const link = document.createElement("a");
            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();

            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        }
    },

    computed: {
        genderIconPath() {
            if (this.selectedHorse.sex === "STALLION") return require("@/assets/icons/gender-male.png");
            if (this.selectedHorse.sex === "MARE") return require("@/assets/icons/gender-female.png");
            if (this.selectedHorse.sex === "GELDING") return require("@/assets/icons/gender-gelding.png");
            return null;
        },
        canDeleteHorse() {
           return HorseUtils.canDeleteHorse(this.selectedHorse?.status, SystemRole.getUserRole());
        },
        canEditHorse() {
            const userRole = SystemRole.getUserRole();
            const horseStatus = this.selectedHorse?.horseStatus;

            const rolePermissions = {
                ADMIN: () => true,
                FIRST_LEVEL_EDITOR: ["NEW_FOAL", "NEW_IMPORTED", "REGISTER_IN_STUDBOOK", "REJECTED", "EXTERNAL"],
                SECOND_LEVEL_EDITOR: ["NEW_FOAL", "NEW_IMPORTED", "WAITING_FOR_MARKING_DATA", "REGISTER_IN_STUDBOOK", "REJECTED", "EXTERNAL"],
                THIRD_LEVEL_EDITOR: ["WAITING_FOR_MARKING_DATA", "LABORATORY_DATA_ENTERED", "REGISTER_IN_STUDBOOK", "REJECTED", "EXTERNAL"],
                FOURTH_LEVEL_EDITOR: ["LABORATORY_DATA_ENTERED", "REGISTER_IN_STUDBOOK", "REJECTED", "EXTERNAL"]
            };

            const permission = rolePermissions[userRole];

            if (!permission) {
                return false;
            } else if (typeof permission === 'function') {
                return permission();
            }

            return permission.includes(horseStatus);
        },
        formattedFullName() {
            const fullName = this.selectedHorse?.fullNameWithCountryCode;
            return fullName
                ? fullName.replace(/\(([^)]*)\)/g, "<span class=\"parenthetical\">($1)</span>")
                : "";
        },
        qrLabHorseUrl() {
            return process.env.VUE_APP_APP_URL + "/laboratory/horses/form/" + this.selectedHorse.id;
        },
        getLabSvgFileName() {
            return  FileUtils.toSafeFileName(this.selectedHorse.name + "_lab_qr_code", "svg", "qrCodeLabHorseDetails")
        },
    },
};
</script>

<style lang="scss">
@import "../../../../assets/theme/mytheme/variables";
.horse-preview-main-data {
    .horse-photo-container {
        position: relative;
        .horse-photo {
            height: auto;
            width: 100%;
            background: var(--primary-color);
            border-radius: 12px;
        }
        .horse-color {
            position: absolute;
            right: 15px;
            bottom: 23px;
            background: var(--primary-button-color);
            color: var(--text-white);
            padding: 5px 10px 3px 10px;
            border-radius: 12px;
            font-size: 18px;
            text-transform: uppercase;
        }
    }
    .horse-name {
        font-family: var(--decor-font-family);
        font-size: 40px;
        font-weight: 600;
        color: var(--text-color);
        .parenthetical {
            opacity: 0.4;
        }
    }
    .arabian-name {
        font-size: 30px;
    }
    .label-and-value {
        display: flex;
        gap: 1rem;
        align-items: center;
    }
    .label {
        font-size: 20px;
        text-transform: uppercase;
    }
    .value {
        font-family: var(--decor-font-family);
        font-size: 30px;
    }
    .gender-icon {
        height: 45px;
    }
    .second-transponder-code {
        justify-content: end;
        width: 100%;
        max-width: 344px;
        span {
            height: 78px;
            overflow-y: auto;
            overflow-wrap: break-word;
            width: 240px;
            &::-webkit-scrollbar {
                width: 8px;
            }
            scrollbar-width: thin;
            scrollbar-color: auto;
        }
    }
}
body.arab-horses {
    .horse-preview-main-data {
        .horse-photo-container {
            position: relative;
            .horse-photo {
                height: 350px;
                width: unset;
                background: var(--primary-color);
                border-radius: 12px;
            }
        }
    }
}

.qr-code-hidden {
    position: absolute;
    left: -9999px;
    visibility: hidden;
    pointer-events: none;
}
</style>
